import React, { useState } from "react";

/**
 * ConflictResolver — renders validation issues with per-issue resolution
 * actions (PRD §9.3).
 *
 * Each issue can be resolved with one of four actions:
 *   - Accept           — the extracted value is correct as-is
 *   - Override         — supply a corrected value
 *   - Escalate         — flag for manager review (logs + future Slack)
 *   - Request Clarification — open a question to the agency (logs + future email)
 *
 * Actions are recorded via the `onResolve(issue, action, payload)` callback.
 * The parent (ReviewPane) wires this to the `resolveConflict` API call.
 * Issues that have been resolved show a "Resolved" badge with the action
 * label and timestamp, and the action buttons are replaced with an
 * "Undo / re-resolve" affordance.
 *
 * @param {{
 *   errors?: Array,
 *   warnings?: Array,
 *   info?: Array,
 *   resolutions?: Array<{ code: string, path?: string, action: string, resolvedAt: string, actor: string }>,
 *   onResolve?: (issue, action, payload) => Promise<{ id?: string }>,
 *   onFieldOverride?: (path, value) => void,  // optional, for override action
 * }} props
 *   Each issue: { code, message, path?, severity, suggestion? }
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build a stable key for matching issues to resolutions. */
function issueKey(issue) {
  return `${issue.code}::${issue.path || ""}`;
}

/** Find the most recent resolution for an issue. */
function findResolution(resolutions, issue) {
  if (!resolutions) return null;
  const key = issueKey(issue);
  // Resolutions are in chronological order; return the most recent match.
  for (let i = resolutions.length - 1; i >= 0; i--) {
    const r = resolutions[i];
    if (`${r.code}::${r.path || ""}` === key) return r;
  }
  return null;
}

const ACTION_LABELS = {
  accept: "Accepted",
  override: "Overridden",
  escalate: "Escalated",
  request_clarification: "Clarification sent",
};

const SEVERITY_META = {
  error: { icon: "✕", className: "severity-error", label: "Error" },
  warning: { icon: "⚠", className: "severity-warning", label: "Warning" },
  info: { icon: "ℹ", className: "severity-info", label: "Info" },
};

// ---------------------------------------------------------------------------
// IssueRow — single issue with action buttons
// ---------------------------------------------------------------------------

function IssueRow({ issue, resolution, onResolve, onFieldOverride, disabled }) {
  const meta = SEVERITY_META[issue.severity] || SEVERITY_META.info;
  const [showOverride, setShowOverride] = useState(false);
  const [showMessage, setShowMessage] = useState(null); // "escalate" | "request_clarification"
  const [overrideValue, setOverrideValue] = useState("");
  const [messageText, setMessageText] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  async function handleAction(action, payload = {}) {
    if (busy || !onResolve) return;
    setBusy(true);
    setLocalError(null);
    try {
      await onResolve(issue, action, payload);
      // Reset local UI state on success.
      setShowOverride(false);
      setShowMessage(null);
      setOverrideValue("");
      setMessageText("");
    } catch (err) {
      setLocalError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  function handleOverride() {
    if (!overrideValue.trim()) {
      setLocalError("Override value cannot be empty");
      return;
    }
    // Try to parse as number for numeric fields, otherwise send as string.
    let value = overrideValue.trim();
    const num = Number(value);
    if (!Number.isNaN(num) && /^-?\d+(\.\d+)?$/.test(value)) {
      value = num;
    }
    handleAction("override", { value });
    if (onFieldOverride && issue.path) {
      onFieldOverride(issue.path, value);
    }
  }

  return (
    <li className={`conflict-row ${meta.className}${resolution ? " resolved" : ""}`}>
      <span className="conflict-icon" aria-label={meta.label}>{meta.icon}</span>
      <div className="conflict-body">
        <div className="conflict-head">
          <span className="conflict-code">{issue.code}</span>
          {issue.path && <span className="conflict-path">{issue.path}</span>}
          {resolution && (
            <span className={`conflict-resolved-badge ${resolution.action}`}>
              {ACTION_LABELS[resolution.action] || resolution.action}
            </span>
          )}
        </div>
        <div className="conflict-message">{issue.message}</div>
        {issue.suggestion && <div className="conflict-suggestion">↳ {issue.suggestion}</div>}

        {/* Resolved metadata */}
        {resolution && (
          <div className="conflict-resolved-meta">
            <span className="mono">
              by {resolution.actor || "system"} · {new Date(resolution.resolvedAt).toLocaleString()}
            </span>
            {resolution.message && (
              <div className="conflict-resolved-note">"{resolution.message}"</div>
            )}
            {resolution.value !== null && resolution.value !== undefined && (
              <div className="conflict-resolved-note">
                <strong>New value:</strong> {String(resolution.value)}
              </div>
            )}
          </div>
        )}

        {/* Action buttons (only when not resolved, or to re-resolve) */}
        {!disabled && onResolve && (
          <div className="conflict-actions">
            {!resolution && (
              <>
                <button
                  className="btn-tiny btn-accept"
                  onClick={() => handleAction("accept")}
                  disabled={busy}
                  title="Accept the extracted value as correct"
                >
                  Accept
                </button>
                {issue.path && (
                  <button
                    className="btn-tiny btn-override"
                    onClick={() => setShowOverride((o) => !o)}
                    disabled={busy}
                    title="Override with a corrected value"
                  >
                    Override
                  </button>
                )}
                <button
                  className="btn-tiny btn-escalate"
                  onClick={() => setShowMessage(showMessage === "escalate" ? null : "escalate")}
                  disabled={busy}
                  title="Flag for manager review"
                >
                  Escalate
                </button>
                <button
                  className="btn-tiny btn-clarify"
                  onClick={() => setShowMessage(showMessage === "request_clarification" ? null : "request_clarification")}
                  disabled={busy}
                  title="Request clarification from the agency"
                >
                  Request Clarification
                </button>
              </>
            )}
            {resolution && (
              <button
                className="btn-tiny btn-undo"
                onClick={() => handleAction("accept", { reResolve: true })}
                disabled={busy}
                title="Re-open this issue for resolution"
              >
                Re-resolve
              </button>
            )}
          </div>
        )}

        {/* Override value input */}
        {showOverride && (
          <div className="conflict-action-form">
            <input
              type="text"
              value={overrideValue}
              onChange={(e) => setOverrideValue(e.target.value)}
              placeholder="Corrected value (number or string)"
              className="conflict-input"
              autoFocus
            />
            <button className="btn btn-primary btn-tiny" onClick={handleOverride} disabled={busy}>
              Apply
            </button>
            <button className="btn btn-tiny" onClick={() => setShowOverride(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        )}

        {/* Escalate / Clarification message input */}
        {showMessage && (
          <div className="conflict-action-form">
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={showMessage === "escalate" ? "Why is this being escalated?" : "What do you need clarified?"}
              className="conflict-textarea"
              rows={2}
              autoFocus
            />
            <button
              className={`btn btn-tiny ${showMessage === "escalate" ? "btn-escalate" : "btn-clarify"}`}
              onClick={() => handleAction(showMessage, { message: messageText.trim() })}
              disabled={busy || !messageText.trim()}
            >
              {showMessage === "escalate" ? "Send Escalation" : "Send Clarification"}
            </button>
            <button className="btn btn-tiny" onClick={() => setShowMessage(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        )}

        {localError && <div className="conflict-error">{localError}</div>}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// ConflictGroup — collapsible bucket
// ---------------------------------------------------------------------------

function ConflictGroup({ title, items, defaultOpen, severityKey, resolutions, onResolve, onFieldOverride, disabled }) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  if (!items || items.length === 0) return null;

  const resolvedCount = items.filter((i) => findResolution(resolutions, i)).length;
  const total = items.length;
  const unresolved = total - resolvedCount;

  return (
    <details className="conflict-group" open={open} onToggle={(e) => setOpen(e.target.open)}>
      <summary className={`conflict-group-summary ${severityKey}`}>
        <span className="conflict-group-title">{title}</span>
        <span className="conflict-group-count">
          {resolvedCount > 0 ? `${resolvedCount}/${total} resolved` : total}
        </span>
      </summary>
      <ul className="conflict-list">
        {items.map((issue, idx) => (
          <IssueRow
            key={`${issue.code}-${issue.path}-${idx}`}
            issue={issue}
            resolution={findResolution(resolutions, issue)}
            onResolve={onResolve}
            onFieldOverride={onFieldOverride}
            disabled={disabled}
          />
        ))}
      </ul>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ConflictResolver({
  errors = [],
  warnings = [],
  info = [],
  resolutions = [],
  onResolve,
  onFieldOverride,
  disabled = false,
}) {
  const total = errors.length + warnings.length + info.length;
  const totalResolved = [errors, warnings, info].reduce((sum, bucket) => {
    return sum + bucket.filter((i) => findResolution(resolutions, i)).length;
  }, 0);

  if (total === 0) {
    return (
      <div className="conflict-resolver">
        <div className="conflict-empty">No validation issues. ✓</div>
      </div>
    );
  }

  return (
    <div className="conflict-resolver">
      {totalResolved > 0 && (
        <div className="conflict-progress">
          <span className="mono">{totalResolved} / {total} issues resolved</span>
          <div className="conflict-progress-bar">
            <div
              className="conflict-progress-fill"
              style={{ width: `${(totalResolved / total) * 100}%` }}
            />
          </div>
        </div>
      )}
      <ConflictGroup
        title="Errors"
        items={errors}
        severityKey="error"
        defaultOpen
        resolutions={resolutions}
        onResolve={onResolve}
        onFieldOverride={onFieldOverride}
        disabled={disabled}
      />
      <ConflictGroup
        title="Warnings"
        items={warnings}
        severityKey="warning"
        defaultOpen
        resolutions={resolutions}
        onResolve={onResolve}
        onFieldOverride={onFieldOverride}
        disabled={disabled}
      />
      <ConflictGroup
        title="Info"
        items={info}
        severityKey="info"
        defaultOpen={false}
        resolutions={resolutions}
        onResolve={onResolve}
        onFieldOverride={onFieldOverride}
        disabled={disabled}
      />
    </div>
  );
}